/* eslint-disable default-case */
/* eslint-disable eqeqeq */
export class Transformer {
  constructor(rules = []) {
    this.rules = rules.filter((r) => r.active);
  }

  transformSingle(key, v, nullValue = null) {
    const rules = this.rules.filter((r) => r.key === key);
    const valore = v.value ? v.origin ?? v.value : v;

    if (rules.length > 0) {
      for (const { operator, action, value, subValue } of rules) {
        let valid = false;

        switch (operator) {
          case "equal": {
            valid = valore == value;
            break;
          }
          case "gt": {
            valid = valore > value;
            break;
          }
          case "gte": {
            valid = valore >= value;
            break;
          }
          case "lt": {
            valid = valore < value;
            break;
          }
          case "lte": {
            valid = valore <= value;
            break;
          }
        }

        if (valid) {
          switch (action) {
            case "substitute": {
              if (v.value) {
                v.value = subValue;
                return v;
              }
              return subValue;
            }
            case "filter": {
              return nullValue;
            }
          }
        }
      }
    }

    return v;
  }

  transform(key, values) {
    if (!values) return [];
    return [...new Set(values.map((v) => this.transformSingle(key, v)))].filter(
      Boolean
    );
  }

  transformX(key, values) {
    const rules = this.rules.filter((r) => r.key === key);

    if (rules.length > 0) {
      return [
        ...new Set(
          values
            .map((v) => {
              const valore = v.value ? v.origin ?? v.value : v;

              for (const { operator, action, value, subValue } of rules) {
                let valid = false;

                switch (operator) {
                  case "equal": {
                    valid = valore == value;
                    break;
                  }
                  case "gt": {
                    valid = valore > value;
                    break;
                  }
                  case "gte": {
                    valid = valore >= value;
                    break;
                  }
                  case "lt": {
                    valid = valore < value;
                    break;
                  }
                  case "lte": {
                    valid = valore <= value;
                    break;
                  }
                }

                if (valid) {
                  switch (action) {
                    case "substitute": {
                      if (v.value) {
                        v.value = subValue;
                        return v;
                      }
                      return subValue;
                    }
                    case "filter": {
                      return null;
                    }
                  }
                }
              }

              return v;
            })
            .filter(Boolean)
        )
      ];
    }

    return values;
  }

  getOrigin(key, value) {
    const rules = this.rules.filter(
      (r) => r.key === key && r.action === "substitute" && r.subValue === value
    );

    if (rules.length > 0) {
      const { operator, value } = rules[0];
      switch (operator) {
        case "lte":
        case "gte":
        case "equal": {
          return value;
        }
        case "gt": {
          return value + 1;
        }

        case "lt": {
          return value - 1;
        }
      }
    }
    return value;
  }
}

export const flattenObjToMap = (obj, parent, res = new Map()) => {
  if (obj === undefined || obj === null) return;

  for (const key of Object.keys(obj)) {
    const propName = (() => {
      if (!isNaN(key)) return parent ?? "";
      return parent ? parent + "." + key : key;
    })();

    if (typeof obj[key] === "object") {
      flattenObjToMap(obj[key], propName, res);
    } else {
      if (!res.has(propName)) res.set(propName, new Set());

      if (Array.isArray(obj[key])) {
        obj[key].forEach((value) => res.get(propName).add(value));
      } else {
        res.get(propName).add(obj[key]);
      }
    }
  }
  return res;
};

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const isPrimitive = (v) => ["string", "number", "boolean"].includes(typeof v);
const asList = (v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]);
const hasType = (schema, type) => asList(schema.type).includes(type);
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isClosed = (schema) =>
  Array.isArray(schema.enum) || hasOwn(schema, "const");

const PRIMITIVE_TYPES = ["string", "number", "integer", "boolean"];
const STRUCTURAL_KEYWORDS = [
  "properties",
  "additionalProperties",
  "patternProperties",
  "items",
  "prefixItems"
];

// Solo $ref locali ("#" o JSON pointer "#/..."), senza rete né $id/$anchor
const resolveSchemaRef = (root, ref) => {
  if (ref !== "#" && !ref.startsWith("#/")) return undefined;

  let pointer;
  try {
    pointer = decodeURIComponent(ref.slice(1));
  } catch (e) {
    return undefined;
  }

  return pointer
    .split("/")
    .slice(1)
    .map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"))
    .reduce(
      (node, token) =>
        node !== null && typeof node === "object" && hasOwn(node, token)
          ? node[token]
          : undefined,
      root
    );
};

const walkSchema = (node, path, ctx, ancestors) => {
  // Frammenti che descrivono lo stesso path: il nodo, i $ref risolti e i rami
  // di allOf/anyOf/oneOf. Un $ref già espanso sul ramo (ciclo) non si riapre.
  const fragments = [];
  const expanded = new Set();
  let opaque = false;

  const collect = (n) => {
    const fragment = n === true ? {} : n;
    if (!isPlainObject(fragment)) return;
    fragments.push(fragment);

    const ref = fragment.$ref;
    if (typeof ref === "string") {
      if (ancestors.has(ref)) opaque = true;
      else if (!expanded.has(ref)) {
        const target = resolveSchemaRef(ctx.root, ref);
        if (target === undefined) {
          ctx.unresolved.add(ref);
          opaque = true;
        } else {
          expanded.add(ref);
          collect(target);
        }
      }
    }

    ["allOf", "anyOf", "oneOf"].forEach((k) =>
      asList(fragment[k]).forEach(collect)
    );
  };

  collect(node);

  const inner = new Set([...ancestors, ...expanded]);

  fragments.forEach((f) => {
    if (isPlainObject(f.properties)) {
      for (const [key, sub] of Object.entries(f.properties)) {
        walkSchema(sub, path ? path + "." + key : key, ctx, inner);
      }
    }
    // Gli array collassano sul path del padre, come in flattenObjToMap
    [...asList(f.items), ...asList(f.prefixItems)].forEach((sub) =>
      walkSchema(sub, path, ctx, inner)
    );
  });

  if (!path || !fragments.length) return;

  // Foglia: un valore primitivo, uno schema libero (se non nasconde un $ref
  // ciclico o non risolto) o un array di cui non si conoscono gli item
  const primitive = fragments.some(
    (f) => isClosed(f) || PRIMITIVE_TYPES.some((t) => hasType(f, t))
  );
  const untyped = !fragments.some(
    (f) =>
      f.type !== undefined ||
      STRUCTURAL_KEYWORDS.some((k) => f[k] !== undefined)
  );
  const unknownItems =
    fragments.some((f) => hasType(f, "array")) &&
    fragments.every(
      (f) => f.items === undefined && f.prefixItems === undefined
    );

  const leaf = primitive || (untyped && !opaque) || unknownItems;
  if (!leaf) return;

  if (!ctx.res.has(path)) ctx.res.set(path, new Set());
  const values = ctx.res.get(path);

  fragments.forEach((f) => {
    if (isClosed(f)) {
      const enumValues = Array.isArray(f.enum) ? f.enum : [];
      [...enumValues, f.const]
        .filter(isPrimitive)
        .forEach((v) => values.add(v));
    } else if (hasType(f, "boolean")) {
      values.add(true).add(false);
    }
  });
};

// Chiavi e valori da un JSON Schema, con gli stessi path di flattenObjToMap.
// I valori arrivano solo dai domini chiusi (enum, const, boolean); i $ref non
// risolvibili in locale finiscono in `unresolved` e non generano chiavi.
export const flattenSchemaToMap = (schema, unresolved = new Set()) => {
  const res = new Map();
  walkSchema(schema, "", { root: schema, res, unresolved }, new Set());
  return res;
};

export const flattenObj = (obj, parent, res = {}) => {
  if (obj === undefined || obj === null) return;

  for (const key of Object.keys(obj)) {
    const propName = (() => {
      if (!isNaN(key)) return parent || "";
      return parent ? parent + "." + key : key;
    })();

    if (typeof obj[key] === "object") {
      flattenObj(obj[key], propName, res);
    } else {
      if (!res[propName]) res[propName] = [];

      if (Array.isArray(obj[key])) {
        obj[key].forEach((value) => res[propName].push(value));
      } else {
        res[propName].push(obj[key]);
      }
    }
  }

  return Object.keys(res)
    .sort()
    .reduce(function (acc, key) {
      acc[key] = res[key].length > 1 ? res[key] : res[key][0];
      return acc;
    }, {});
};

export const mapToObj = (map = new Map(), obj = {}) => {
  for (const [key, value] of map.entries()) {
    if (!obj[key])
      obj[key] = {
        id: key,
        active: true,
        values: [],
        dbNestedFields: key.includes(".")
      };
    const values = [...new Set([...value, ...obj[key].values])];
    obj[key].values = values;
  }

  return Object.keys(obj)
    .sort()
    .reduce(function (acc, key) {
      acc[key] = obj[key];
      return acc;
    }, {});
};
